import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import { useParams } from "@tanstack/react-router";
import { GripHorizontalIcon, SparklesIcon } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, type KeyboardEvent, type PointerEvent } from "react";
import { createPortal } from "react-dom";

import ChatView from "./ChatView";
import { Button } from "./ui/button";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "../lib/touchTarget";
import {
  clampProjectLeadCompanionPosition,
  shouldRenderProjectLeadCompanion,
  type ProjectLeadCompanionDock,
  type ProjectLeadCompanionPosition,
} from "../projectLeadCompanionState";
import { useUiStateStore } from "../uiStateStore";
import { resolveThreadRouteRef } from "../threadRoutes";
import {
  companionFrameStyle,
  companionPlacementClass,
  companionUsableViewportHeight,
  useProjectLeadCompanionPortal,
  type CompanionLayoutInput,
} from "./projectLeadCompanionLayout";
import { ProjectLeadCompanionControls } from "./ProjectLeadCompanionControls";

interface ProjectLeadCompanionFrameProps extends CompanionLayoutInput {
  readonly threadRef: ScopedThreadRef;
  readonly minimized: boolean;
  readonly dock: ProjectLeadCompanionDock;
  readonly position: ProjectLeadCompanionPosition | null;
  readonly onClose: () => void;
  readonly onMinimizedChange: (minimized: boolean) => void;
  readonly onDockChange: (dock: ProjectLeadCompanionDock) => void;
  readonly onPositionChange: (position: ProjectLeadCompanionPosition) => void;
}

interface DragState {
  readonly pointerId: number;
  readonly offsetX: number;
  readonly offsetY: number;
}

export function ProjectLeadCompanionFrame(props: ProjectLeadCompanionFrameProps) {
  const panelRef = useRef<HTMLElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const { dock, minimized, position, onPositionChange } = props;
  const usableViewportHeight = companionUsableViewportHeight(props);

  useLayoutEffect(() => {
    if (props.surface === "task-dialog" || minimized || dock !== "floating" || position === null)
      return;
    const reclamp = () => {
      const rect = panelRef.current?.getBoundingClientRect();
      if (!rect) return;
      const clamped = clampProjectLeadCompanionPosition({
        position,
        viewportWidth: window.innerWidth,
        viewportHeight: usableViewportHeight,
        width: rect.width,
        height: rect.height,
      });
      if (clamped.x !== position.x || clamped.y !== position.y) onPositionChange(clamped);
    };
    reclamp();
    window.addEventListener("resize", reclamp);
    return () => window.removeEventListener("resize", reclamp);
  }, [dock, minimized, position, onPositionChange, props.surface, usableViewportHeight]);

  const moveBy = (deltaX: number, deltaY: number) => {
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    props.onPositionChange(
      clampProjectLeadCompanionPosition({
        position: { x: rect.left + deltaX, y: rect.top + deltaY },
        viewportWidth: window.innerWidth,
        viewportHeight: usableViewportHeight,
        width: rect.width,
        height: rect.height,
      }),
    );
  };

  const handleTitleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (props.surface === "task-dialog" || props.dock !== "floating") return;
    const distance = event.shiftKey ? 48 : 16;
    const delta =
      event.key === "ArrowLeft"
        ? { x: -distance, y: 0 }
        : event.key === "ArrowRight"
          ? { x: distance, y: 0 }
          : event.key === "ArrowUp"
            ? { x: 0, y: -distance }
            : event.key === "ArrowDown"
              ? { x: 0, y: distance }
              : null;
    if (!delta) return;
    event.preventDefault();
    moveBy(delta.x, delta.y);
  };

  const handlePointerDown = (event: PointerEvent<HTMLElement>) => {
    if (
      props.surface === "task-dialog" ||
      props.dock !== "floating" ||
      (event.target as HTMLElement).closest("[data-no-drag]")
    )
      return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    const panel = panelRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !panel) return;
    const rect = panel.getBoundingClientRect();
    props.onPositionChange(
      clampProjectLeadCompanionPosition({
        position: { x: event.clientX - drag.offsetX, y: event.clientY - drag.offsetY },
        viewportWidth: window.innerWidth,
        viewportHeight: usableViewportHeight,
        width: rect.width,
        height: rect.height,
      }),
    );
  };

  if (props.minimized) {
    return (
      <Button
        className={`${RESPONSIVE_TEXT_BUTTON_CLASS} ${
          props.surface === "task-dialog"
            ? "h-full w-full border-border bg-card shadow-none"
            : "fixed right-4 bottom-4 z-50 shadow-xl"
        }`}
        variant="outline"
        onClick={() => props.onMinimizedChange(false)}
      >
        <SparklesIcon className="text-primary" aria-hidden /> Project Lead
      </Button>
    );
  }

  const floatingStyle = companionFrameStyle(props);
  const placementClass = companionPlacementClass(props);

  return (
    <section
      ref={panelRef}
      aria-label="Project Lead companion"
      className={`${
        props.surface === "task-dialog"
          ? "z-0 shadow-none"
          : "fixed z-50 shadow-2xl shadow-black/30"
      } flex min-h-0 overflow-hidden bg-card text-foreground ${
        props.surface === "task-dialog"
          ? "rounded-none border-0"
          : "rounded-xl border border-border"
      } ${placementClass}`}
      style={floatingStyle}
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header
          aria-label={
            props.surface === "task-dialog"
              ? "Project Lead companion header"
              : "Move Project Lead companion"
          }
          className={`flex min-h-touch shrink-0 items-center gap-2 border-b border-border bg-muted px-3 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring md:h-11 md:min-h-0 md:px-3 ${
            props.surface === "task-dialog" ? "cursor-default" : "cursor-move"
          }`}
          onKeyDown={handleTitleKeyDown}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={() => {
            dragRef.current = null;
          }}
          tabIndex={0}
        >
          {props.surface === "task-dialog" ? null : (
            <GripHorizontalIcon className="size-4 text-muted-foreground" aria-hidden />
          )}
          <SparklesIcon className="size-4 text-primary" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">Project Lead</p>
            <p className="text-[11px] text-muted-foreground">Project context connected</p>
          </div>
          <ProjectLeadCompanionControls
            surface={props.surface}
            dock={props.dock}
            onClose={props.onClose}
            onMinimize={() => props.onMinimizedChange(true)}
            onDockChange={props.onDockChange}
          />
        </header>
        <div className="flex min-h-0 flex-1 bg-background [&_h1]:text-xl">
          <ChatView
            environmentId={props.threadRef.environmentId}
            threadId={props.threadRef.threadId}
            routeKind="server"
            showRightPanelLauncher={false}
          />
        </div>
      </div>
    </section>
  );
}

export function ProjectLeadCompanionHost() {
  const companion = useUiStateStore((state) => state.projectLeadCompanion);
  const close = useUiStateStore((state) => state.closeProjectLeadCompanion);
  const setMinimized = useUiStateStore((state) => state.setProjectLeadCompanionMinimized);
  const setDock = useUiStateStore((state) => state.setProjectLeadCompanionDock);
  const setPosition = useUiStateStore((state) => state.setProjectLeadCompanionPosition);
  const focusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const modalPortal = useProjectLeadCompanionPortal();
  const routeThreadRef = useParams({
    strict: false,
    select: (params) => resolveThreadRouteRef(params),
  });

  useEffect(() => {
    if (companion.open && !wasOpenRef.current) {
      focusRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      requestAnimationFrame(() =>
        document
          .querySelector<HTMLElement>(
            '[aria-label="Move Project Lead companion"], [aria-label="Project Lead companion header"]',
          )
          ?.focus(),
      );
    }
    if (!companion.open && wasOpenRef.current) focusRef.current?.focus();
    wasOpenRef.current = companion.open;
  }, [companion.open]);

  const frame =
    shouldRenderProjectLeadCompanion({ companion, routeThreadRef }) && companion.threadRef ? (
      <ProjectLeadCompanionFrame
        surface={modalPortal === null ? "global" : "task-dialog"}
        threadRef={companion.threadRef}
        minimized={companion.minimized}
        dock={companion.dock}
        position={companion.position}
        onClose={close}
        onMinimizedChange={setMinimized}
        onDockChange={setDock}
        onPositionChange={setPosition}
      />
    ) : null;
  if (frame === null) return null;
  return modalPortal === null ? frame : createPortal(frame, modalPortal);
}
